import { useState } from 'react'

import { Button } from '../../components/button'
import { Card, CardTitle } from '../../components/card'
import { Modal } from '../../components/modal'
import { StepUpDialog } from '../../components/step-up'
import { ago } from '../../app/format'
import { t } from '../../copy'
import { T } from '../../copy/text'
import {
  useLoginDevices,
  useRevokeLoginDevice,
  useRevokeOtherLoginDevices,
} from '../../app/queries'
import type { Schemas } from '@npc/api-client'

type Device = Schemas['LoginDevice']

/**
 * 「设置 · 登录设备」。
 *
 * ------------------------------------------------------------------
 * # 三个问题，顺序就是他会问的顺序
 *
 *	我现在在哪几台上登着   列表，当前这台排第一并且**标出来**
 *	有没有我不认得的       每条给设备名、地址、最近用过多久
 *	不认得的怎么办         一台一台踢，或者一次全踢掉（除了这台）
 *
 * # 两个动作的门槛刻意不一样
 *
 *	踢掉某一台   要当场再验一次，令牌绑到那条 sessionId 上。
 *	             不绑的话，一次为小事做的确认能拿去把他所有设备
 *	             连同他自己一起踢下线
 *	全退掉       **一道门都没有**。这是「我怀疑号被人登了」时唯一有用的
 *	             动作，而收紧安全的动作不该被安全措施挡住
 *
 * 退出自己这台走底下那个「退出登录」，也是零门槛——日常操作不加门槛。
 *
 * # 「换电脑」那句话为什么必须在这儿
 *
 * arron 的要求是「超级管理员可在其他设备拥有同等管理权，换电脑需人工登录绑定」。
 * 他看着这张表的时候会问「那我换台电脑要不要先在这里加一台」——
 * 答案是不用，登一次就行。同一句话里还要说清**我们刻意不做「记住这台电脑」**：
 * 一条能被制造出来的旁路等于没有门（CLAUDE.md §8）。
 * ------------------------------------------------------------------
 */
export function LoginDevices() {
  const devices = useLoginDevices()
  const revoke = useRevokeLoginDevice()
  const revokeOthers = useRevokeOtherLoginDevices()

  /** 正要踢掉的那一台。先确认，确认完才弹二次验证。 */
  const [pending, setPending] = useState<Device | null>(null)
  const [stepUpFor, setStepUpFor] = useState<Device | null>(null)
  const [done, setDone] = useState<string | null>(null)
  /** 动作本身失败了（令牌过期、那台已经自己退了、库连不上）。 */
  const [failure, setFailure] = useState<string | null>(null)

  const list = devices.data?.devices ?? []
  const others = list.filter((one) => !one.current)

  return (
    <Card className="mb-npc-card max-w-[560px]" data-testid="settings-devices">
      <CardTitle className="mb-npc-grid-sm block">
        <T id="account.devices.heading" />
      </CardTitle>
      <p className="m-0 mb-npc-card text-npc-body-sm leading-[1.7] text-npc-ink-soft">
        <T id="account.devices.hint" />
      </p>

      {devices.isPending ? (
        <p className="m-0 text-npc-body-sm text-npc-ink-soft" data-testid="devices-loading">
          <T id="nav.common.loading" />
        </p>
      ) : devices.error ? (
        <div data-testid="devices-error">
          <p role="alert" className="m-0 mb-npc-gap-sm text-npc-body-sm text-npc-red-ink">
            {devices.error instanceof Error ? devices.error.message : t('error.generic')}
          </p>
          <Button size="sm" onClick={() => void devices.refetch()}>
            <T id="error.read_failed.retry" />
          </Button>
        </div>
      ) : (
        <>
          <ul className="m-0 list-none p-0" data-testid="devices-list">
            {list.map((one) => (
              <DeviceRow
                key={one.id}
                device={one}
                onRevoke={() => {
                  setDone(null)
                  setPending(one)
                }}
              />
            ))}
          </ul>

          {others.length === 0 ? (
            <p className="mb-0 mt-npc-gap-sm text-npc-caption text-npc-ink-softer" data-testid="devices-empty">
              <T id="account.devices.empty" />
            </p>
          ) : (
            <Button
              size="sm"
              className="mt-npc-card"
              disabled={revokeOthers.isPending}
              data-testid="devices-revoke-others"
              onClick={() => {
                setDone(null)
                setFailure(null)
                revokeOthers
                  .mutateAsync()
                  .then((out) => {
                    setDone(
                      out.revoked > 0
                        ? t('account.devices.revoke.others.done', { count: String(out.revoked) })
                        : t('account.devices.revoke.others.none'),
                    )
                  })
                  .catch((error: unknown) => {
                    setFailure(error instanceof Error ? error.message : t('error.generic'))
                  })
              }}
            >
              <T id="account.devices.revoke.others.cta" />
            </Button>
          )}

          {done ? (
            <p className="mb-0 mt-npc-gap-sm text-npc-caption text-npc-green-deep" data-testid="devices-done">
              {done}
            </p>
          ) : null}

          {/* **失败要说出来。** 一个点完什么都不变的按钮，
              比一个报错的按钮糟得多：他会以为踢掉了，而那台还登着。 */}
          {failure ? (
            <p role="alert" className="mb-0 mt-npc-gap-sm text-npc-caption text-npc-red-ink" data-testid="devices-failure">
              {failure}
            </p>
          ) : null}
        </>
      )}

      <p className="mb-0 mt-npc-card text-npc-caption leading-[1.7] text-npc-ink-softer">
        <T id="account.devices.admin_note" />
      </p>

      {/* 先问一句「真要踢吗」，**再**问「是不是你本人」。
          顺序反过来的话，他会先被要求输密码，然后才知道自己要确认的是什么。 */}
      <Modal
        open={pending !== null}
        onOpenChange={(next) => {
          if (!next) setPending(null)
        }}
        title={t('account.devices.revoke.confirm.title')}
        description={pending?.name}
        footer={
          <>
            <Button variant="ghost" onClick={() => setPending(null)}>
              <T id="account.stepup.cancel" />
            </Button>
            <Button
              variant="danger"
              data-testid="devices-revoke-confirm"
              onClick={() => {
                setStepUpFor(pending)
                setPending(null)
              }}
            >
              <T id="account.devices.revoke.cta" />
            </Button>
          </>
        }
      >
        <p className="m-0 text-npc-body-sm leading-[1.7] text-npc-ink-soft">
          <T id="account.devices.revoke.confirm.body" />
        </p>
      </Modal>

      <StepUpDialog
        open={stepUpFor !== null}
        intent="manage_authentication"
        resource={stepUpFor?.id ?? ''}
        description={stepUpFor ? t('account.devices.revoke.confirm.body') : undefined}
        onCancel={() => setStepUpFor(null)}
        onVerified={(token) => {
          const target = stepUpFor
          setStepUpFor(null)
          if (!target) return
          setFailure(null)
          revoke
            .mutateAsync({ sessionId: target.id, stepUpToken: token })
            .then(() => setDone(t('account.devices.revoke.done')))
            .catch((error: unknown) => {
              // 令牌是一次性的，走到这儿它已经烧掉了——所以这里**不重试**，
              // 也不悄悄再弹一次框。说清楚，让他自己决定要不要再来一遍。
              setFailure(error instanceof Error ? error.message : t('error.generic'))
            })
        }}
      />
    </Card>
  )
}

function DeviceRow({ device, onRevoke }: { device: Device; onRevoke: () => void }) {
  return (
    <li
      data-testid={device.current ? 'device-row-current' : 'device-row'}
      className="flex flex-col gap-npc-gap-sm border-b border-npc-border-soft py-npc-grid-sm last:border-b-0 sm:flex-row sm:items-center sm:justify-between"
    >
      <div className="min-w-0">
        <p className="m-0 text-npc-section-sm font-medium text-npc-ink-strong">
          {device.name}
          {device.current ? (
            <span
              data-testid="device-current-badge"
              className="ml-npc-gap-sm rounded-npc-chip bg-npc-green-tint px-[8px] py-[2px] text-npc-meta font-normal text-npc-green-deep"
            >
              <T id="account.devices.current" />
            </span>
          ) : null}
        </p>
        <p className="m-0 mt-[3px] text-npc-caption text-npc-ink-soft">
          {device.sign_in_method_text}
          {' · '}
          <T id="account.devices.last_used" vars={{ when: ago(device.last_used_at) }} />
        </p>
        <p className="m-0 mt-[2px] text-npc-meta text-npc-ink-softer">
          <T id="account.devices.signed_in" vars={{ when: ago(device.signed_in_at) }} />
          {device.ip ? <span className="ml-npc-gap-sm font-npc-mono">{device.ip}</span> : null}
        </p>
      </div>
      {device.current ? null : (
        <Button size="sm" data-testid="device-revoke" onClick={onRevoke} className="shrink-0 self-start sm:self-auto">
          <T id="account.devices.revoke.cta" />
        </Button>
      )}
    </li>
  )
}
